import { selectProjectsIds, selectProjectStatus, useAppSelector } from '@core/core/store'
import { useLatest, useStorage } from '@library/hooks'
import { intersection } from 'common/helpers'
import type { Nullable, ProjectId } from 'common/types'
import { useCallback } from 'react'

const SEPARATOR = ','
const INITIAL_FAVORITE: ProjectId[] = []

function convertFromStorage(value: Nullable<string>): ProjectId[] {
  return value?.split(SEPARATOR) ?? INITIAL_FAVORITE
}

function convertToStorage(ids: ProjectId[]) {
  return ids.join(SEPARATOR)
}

const clearDeleted = (newFavorite: ProjectId[], loaded: ProjectId[], isLoaded: boolean) => {
  if (!isLoaded) return newFavorite

  return Array.from(intersection(new Set(newFavorite), new Set(loaded)))
}

export function useFavorite() {
  const projectIds = useLatest(useAppSelector(selectProjectsIds))
  const isProjectLoaded = useLatest(useAppSelector(selectProjectStatus) === 'done')
  const [favorite, setFavorite] = useStorage(
    'geomate-favorite-projects',
    convertFromStorage,
    convertToStorage,
    INITIAL_FAVORITE,
  )

  const toggleFavorite = useCallback(
    (id: ProjectId) => {
      setFavorite(prev => {
        const prevSet = new Set(prev)
        const deleted = prevSet.delete(id)
        if (!deleted) {
          prevSet.add(id)
        }

        return clearDeleted(Array.from(prevSet), projectIds.current, isProjectLoaded.current)
      })
    },
    [setFavorite],
  )

  const isFavorite = useCallback((id: ProjectId) => favorite.includes(id), [favorite])

  return { isFavorite, toggleFavorite }
}
